import { useCallback, useMemo } from 'react';

import type { FeatureFlagNames } from 'api/configuration/features/FeatureFlagConfigurationSetting';
import { useConfigurationService } from 'api/configuration/hooks/useConfigurationService';
import type { AppConfiguration } from 'api/configuration/types';
import { isLocalDev } from 'utils/env';
import { hasValue } from 'utils/values';

import { useFeatureFlagsStore } from '../state/featureFlagsStore';
import { featureFlagsOverridesState } from '../utils/getFeatureFlagsOverrides';

/**
 * return a function to reset feature flags to useFeatureFlagsStore to the environment configuration.
 */
export function useResetFeatureFlagsStore() {
  const initialize = useFeatureFlagsStore((state) => state.initialize);

  const { data, isLoading } = useConfigurationService(selectFeatureFlags);
  const resolvedFeatureFlags = useMemo(() => featureFlagsOverridesState || data || [], [data]);

  const isFetched = isLocalDev || (!isLoading && hasValue(data));

  return useCallback(
    (overrides?: FeatureFlagNames[]) => {
      if (!isFetched) {
        return;
      }

      const locked = hasValue(overrides);

      const combinedFeatureFlags = resolvedFeatureFlags.map((featureFlag) => ({
        ...featureFlag,
        enabled: overrides?.includes(featureFlag.name) ?? featureFlag.enabled
      }));

      initialize(combinedFeatureFlags, locked);
    },
    [initialize, isFetched, resolvedFeatureFlags]
  );
}

function selectFeatureFlags(state: AppConfiguration) {
  return state.features.map((name) => ({
    name,
    enabled: true
  }));
}
